import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Divider,
  Typography,
  Tabs,
  Tab,
  Fade,
  Button,
} from "@mui/material";
import Question from "../Results/Message/Question";
import { makeStyles } from "tss-react/mui";
import { GeneralDomain } from "./SourcesList";
import { useSelector } from "react-redux";
import DotsLoader from "../../../../components/DotsLoader";
import ParseAnswer from "../Results/Message/Answer/ParseAnswer/ParseAnswer";
import {
  FactCheckRounded,
  FactCheckOutlined,
  ChevronRightRounded,
  WorkspacesFilled,
} from "@mui/icons-material";
import { MediationRounded, MediationOutlined } from "@mui/icons-material";
import { AssistantRounded, AssistantOutlined } from "@mui/icons-material";
import RelatedDocs from "../Results/Message/RelatedDocs/RelatedDocs";
import { GradientIconAccent } from "../../../../components/GradientIcon";
import ThreeDotsLoader from "../../../../components/ThreeDotsLoader";

const useStyles = makeStyles()((theme) => ({
  customTabs: {
    minHeight: 36,
    marginTop: 2,
    "& .MuiTabs-indicator": {
      height: 4,
      borderRadius: "50px 50px 0px 0px",
      backgroundColor:
        theme.palette.mode === "dark" ? theme.palette.primary.main : "#9C27B0",
    },
    "& .MuiTab-root": {
      minHeight: 36,
      paddingTop: 6,
      paddingBottom: 6,
    },
    "& .MuiTabs-flexContainer": {
      minHeight: 36,
      paddingTop: 6,
      paddingBottom: 4,
    },
    "& .MuiTab-root.Mui-selected": {
      color:
        theme.palette.mode === "dark" ? theme.palette.primary.main : "#9C27B0",
    },
  },
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  relatedDocsContainer: {
    marginBottom: 24,
    height: 54,
    width: "100%",
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.light
        : "#FDF0FF",
    borderRadius: 16,
    padding: "10px 8px 10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  viewAllRelatedDocsBtn: {
    color: theme.palette.mode === "dark" ? theme.palette.text.main : "#7B1FA2",
    textTransform: "none",
  },
}));

function StreamMessage({
  tempQandA = null,
  isMetadataLoading = false,
  queryContainerHeight = 0,
  relatedDocs = [],
  setSelectedRelatedDoc = () => {},
  onIsolationMarkupChange = () => {},
  onSelectedIsolationMarkupChange = () => {},
}) {
  const { classes } = useStyles();
  const [selectedTab, setSelectedTab] = useState("ai_answer");
  const [chatAgent, setChatAgent] = useState(
    tempQandA ? tempQandA["chatAgent"] : "qna"
  );
  const { thinkingMode } = useSelector((state) => state.account);

  // Track content changes - show loader when content length stays the same
  const [isContentStalling, setIsContentStalling] = useState(false);
  const previousContentLength = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!tempQandA?.["response"]) {
      setIsContentStalling(false);
      previousContentLength.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    const currentLength = tempQandA["response"].length;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If we have content and length has changed, we're actively streaming
    if (currentLength > 0 && currentLength !== previousContentLength.current) {
      // Content is updating - not stalling
      setIsContentStalling(false);
      previousContentLength.current = currentLength;

      // After content updates, set a flag to show it might stall soon
      // This creates a brief pause before showing the loader
      timeoutRef.current = setTimeout(() => {
        setIsContentStalling(true);
      }, 400);
    } else if (
      currentLength === previousContentLength.current &&
      previousContentLength.current !== null
    ) {
      // Content length stayed the same - content is stalling
      setIsContentStalling(true);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [tempQandA?.["response"]]);

  // Get sources count for the tab badge
  const citations = tempQandA?.response?.content_object?.citations || [];
  const sourcesCount = citations.length;
  const hasResponse = tempQandA?.response;

  // Check if this is an entity search (don't show sources tab for entities)
  const isEntitySearch = chatAgent === "checklist_search";

  function getLoaderMessage(isMetadataPhase = false) {
    if (isMetadataPhase) {
      return "Finalising Sources";
    }

    switch (tempQandA["chatAgent"]) {
      case "checklist_search": {
        return "Finding the document";
      }

      case "data_analysis": {
        return "Analyzing";
      }

      case "qna": {
        return "Retrieving Answer";
      }

      default: {
        return "Retrieving Answer";
      }
    }
  }

  const isSourcesTabDisabled =
    !hasResponse ||
    isMetadataLoading ||
    (hasResponse && !isMetadataLoading && sourcesCount === 0);

  return (
    <Box
      sx={{
        minHeight: `calc(100vh - ${197 + queryContainerHeight}px)`,
      }}>
      <Question query={{ content_object: { text: tempQandA["query"] } }} />
      <Box
        mt={2}
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}>
        <Tabs
          value={selectedTab}
          onChange={(_, value) => setSelectedTab(value)}
          className={classes.customTabs}>
          <Tab
            label="AI Answer"
            value={"ai_answer"}
            iconPosition="start"
            icon={
              selectedTab === "ai_answer" ? (
                <AssistantRounded fontSize="small" />
              ) : (
                <AssistantOutlined fontSize="small" />
              )
            }
          />
          {/* <Tab
            label={`Sources ${isSourcesTabDisabled ? "" : `(${sourcesCount})`}`}
            disabled={isSourcesTabDisabled}
            value={"sources"}
            iconPosition="start"
            icon={
              selectedTab === "sources" ? (
                <MediationRounded fontSize="small" />
              ) : (
                <MediationOutlined fontSize="small" />
              )
            }
          /> */}
          {relatedDocs.length > 0 && (
            <Tab
              label="Similar Files"
              value={"related_docs"}
              iconPosition="start"
              icon={
                selectedTab === "related_docs" ? (
                  <FactCheckRounded fontSize="small" />
                ) : (
                  <FactCheckOutlined fontSize="small" />
                )
              }
            />
          )}
        </Tabs>
        <Box />
      </Box>
      <Divider />
      <Box mt={2} display="flex" flexDirection="column" alignItems="flex-start">
        {/* Answer Tab Content */}
        {selectedTab === "ai_answer" && (
          <>
            {relatedDocs.length > 0 && (
              <Box className={classes.relatedDocsContainer}>
                <Typography>
                  Found <b>{relatedDocs.length}</b> Related Doc
                  {relatedDocs.length > 1 ? "s" : ""}
                </Typography>
                <Button
                  onClick={() => setSelectedTab("related_docs")}
                  variant="text"
                  size="small"
                  endIcon={<ChevronRightRounded />}
                  className={classes.viewAllRelatedDocsBtn}>
                  View All Results
                </Button>
              </Box>
            )}
            {!tempQandA["response"] ? (
              <Fade in={!tempQandA["response"]} timeout={400}>
                <Box display="flex" alignItems="center">
                  <ThreeDotsLoader />
                  <Box ml={1.5}>
                    <Typography
                      variant="body1"
                      className={classes.gradientText}
                      sx={{ opacity: 0.8 }}>
                      Pulling the right info together
                      <span className="loadingStyle"></span>
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            ) : null}
            {tempQandA["response"] ? (
              <Box width="100%">
                <ParseAnswer
                  isStreaming={true}
                  answer={{
                    content_object: { text: tempQandA["response"] || "" },
                  }}
                  onIsolationMarkupChange={onIsolationMarkupChange}
                  onSelectedIsolationMarkupChange={onSelectedIsolationMarkupChange}
                />

                {/* Show loading indicator when content is stalling */}
                {isContentStalling &&
                  tempQandA["response"] &&
                  !isMetadataLoading && (
                    <Box mt={0} display="flex" alignItems="center">
                      <ThreeDotsLoader />
                      <Box ml={1}>
                        <Typography
                          variant="body1"
                          className={classes.gradientText}
                          sx={{ opacity: 0.8 }}>
                          Thinking
                          <span className="loadingStyle"></span>
                        </Typography>
                      </Box>
                    </Box>
                  )}

                {isMetadataLoading && (
                  <Box mt={1} display="flex" alignItems="center">
                    {/* <BreathingBlobLoader size={32} /> */}
                    <ThreeDotsLoader />
                    <Box ml={1}>
                      <Typography
                        variant="body1"
                        className={classes.gradientText}
                        sx={{ opacity: 0.8 }}>
                        {getLoaderMessage(true)}
                        <span className="loadingStyle"></span>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : null}
          </>
        )}
        {/* Sources Tab Content */}
        {/* {selectedTab === "sources" && !isEntitySearch && (
          <Box width="100%">
            {isMetadataLoading ? (
              <Box display="flex" alignItems="center">
                <DotsLoader size={36} />
                <Box ml={1}>
                  <Typography variant="body1" className={classes.gradientText}>
                    {getLoaderMessage(true)}
                    <span className="loadingStyle"></span>
                  </Typography>
                </Box>
              </Box>
            ) : sourcesCount === 0 ? (
              <GeneralDomain />
            ) : (
              <Typography variant="body2" color="textSecondary">
                Sources will be displayed here once available.
              </Typography>
            )}
          </Box>
        )} */}
        {selectedTab === "related_docs" && relatedDocs.length > 0 && (
          <Box width="100%">
            <RelatedDocs
              relatedDocsFromStreaming={relatedDocs}
              setSelectedRelatedDoc={setSelectedRelatedDoc}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default StreamMessage;
